import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { PopupEvent } from 'webapp/enums/enums';
import { isAlive } from 'webapp/utils/common';

import joinClasses from 'webapp/helpers/join-classes';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import ToolTipComponent from 'webapp/components/tool-tip';

import type ScriptItem from 'webapp/models-custom/script-item';
import { ScriptItemGeneralType } from 'webapp/models-custom/script-item';

interface Args {
	model: ScriptItem;
	isMksTimezoneWarningShown: boolean;
	onDelete: () => void;
	onBracketChange: (item: ScriptItem, isSelect: boolean) => void;
}

export default class ScriptsFormCodeItemComponent extends Component<Args> {
	@service('model-manager/scripts') declare mmScriptsService: Service.ModelManager.Scripts;
	@service('constants') declare constantsService: Service.Constants;
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;

	@tracked isContextActive = false;

	private rootNode: HTMLElement | null = null;

	get mkTimezoneWarningText() {
		let mkTimezoneWarningText = '';
		const source = this.metricSetup?.source;

		if (source && this.args.isMksTimezoneWarningShown && !this.args.model.previewIsLoading) {
			const timezone = this.constantsService.timezones.find(
				(item) => item.value === source.timezone,
			);
			if (timezone) {
				mkTimezoneWarningText = `Data from ‘${source.title}’ is stored in timezone ${timezone.value} (${timezone.gmtOffset}).`;
			}
		}

		return mkTimezoneWarningText;
	}

	get metricSetup() {
		return this.args.model.dataSelect?.metricSetup;
	}

	@action
	editItem() {
		if (this.isContextActive) {
			return;
		}

		const attachTo = this.rootNode?.querySelector('a.script-item__box') as HTMLElement | null;

		if (!attachTo) {
			return;
		}

		const model = this.args.model,
			isMath = model.isMath;

		let popup;

		if (isMath) {
			const props: PopupData.Script.OptionsMath = {
				model: model,
				changeBrackets: (item, isSelect) => {
					if (item) {
						this.args.onBracketChange(item, isSelect);
					}
				},
			};
			popup = this.mmScriptsService.openScriptMathOptions(attachTo, props);
		} else {
			const props: PopupData.Script.OptionsMetric = {
				model: model,
				openAdvanced: (tmpDs, isTemporary) => {
					tmpDs.applyBufferedChanges(); // in case we did not "save" it yet, we still want our progress shown in advanced

					const tmpDsCopy = this.mmDataSelectsService.createModelFromRecord(tmpDs.model);
					const advancedPopup = this.mmDataSelectsService.openAdvancedPopup({
						dataSelect: tmpDsCopy,
						isScriptAdvanced: true,
						sourceForTracking: 'scripts',
						saveFn: (ds) => {
							model.type = ScriptItemGeneralType.METRIC;
							this.mmScriptsService.updateDataSelectFromAnother(
								model.dataSelect!,
								ds,
							);
							model.dataSelect!.isScriptStatic = ds.isScriptStatic;
							ds.applyBufferedChanges(ds.dirtyProperties); // argument to force instant apply instead of next runloop bcos of discard
						},
					});

					advancedPopup.on(PopupEvent.CLOSED, this, () => {
						if (isTemporary) {
							void tmpDs.delete();
						}
					});
				},
				deleteItem: () => {
					this.deleteMetric();
				},
			};
			popup = this.mmScriptsService.openScriptMetricOptions(attachTo, props);
		}

		if (popup) {
			this.isContextActive = true;
			popup.on(PopupEvent.CLOSED, this, () => {
				if (isAlive(this)) {
					this.isContextActive = false;
				}
			});
		}
	}

	@action
	deleteMetric() {
		this.args.onDelete();
	}

	@action
	setupView(node: HTMLElement) {
		this.rootNode = node;

		if (this.args.model.autoOpen) {
			this.args.model.autoOpen = false;
			this.editItem();
		}
	}
	<template>
		<div
			class={{joinClasses (array "script-item" (if this.isContextActive "active"))}}
			{{didInsert this.setupView}}
			{{didUpdate
				@model.scheduleUpdateValue
				@model.value
				@model.dataSelect.metricSetupKey
				@model.dataSelect.metricSetupSpaceAccessId
				@model.dataSelect.metricSetupDimensions
				@model.dataSelect.metricSetup.attributeNamesFromBasicFilters
				@model.dataSelect.unitName
				@model.dataSelect.dateRangeCM.formula
				@model.dataSelect.isScriptStatic
			}}
		>
			{{#if @model.isConst}}
				<div class="type-metric type-const">
					<a role="button" class="script-item__box" {{on "click" this.editItem}}>
						<span>{{@model.outputInUI}}</span>
					</a>
				</div>
			{{else if @model.isMath}}
				<div class="type-math">
					<a
						role="button"
						class="script-item__box math-{{@model.mathType}}"
						{{on "click" this.editItem}}
					></a>
				</div>
			{{else if @model.isBracket}}
				<div class="type-bracket">
					{{@model.outputInUI}}
				</div>
			{{else if @model.isMk}}
				<div class="type-metric type-mk">
					<a role="button" class="script-item__box" {{on "click" this.editItem}}>
						<img
							src={{this.metricSetup.source.realAvatar}}
							alt={{this.metricSetup.source.title}}
						/>
						<span>{{this.metricSetup.source.title}}</span>
						<strong>{{this.metricSetup.metric.name}}</strong>
						<ToolTipComponent
							@text={{this.metricSetup.metric.name}}
							@maxWidth={{300}}
						/>
					</a>
					<span class="result {{if @model.previewIsLoading 'loading'}}">
						<span class="value">
							{{@model.previewString}}
							{{#if @model.previewString}}
								<ToolTipComponent
									@text={{@model.previewString}}
									@position="bottom"
								/>
							{{/if}}
						</span>

						{{#if this.mkTimezoneWarningText}}
							<span class="timezone-warning">
								<CommonUiIconComponent @name="AlertFill" @size="sm" />
								<ToolTipComponent
									@text={{this.mkTimezoneWarningText}}
									@maxWidth={{250}}
									@position="bottom"
								/>
							</span>
						{{/if}}
					</span>
				</div>
			{{else if @model.isPlaceholder}}
				<div class="type-placeholder">
					<a role="button" class="script-item__box" {{on "click" this.editItem}}>
						{{@model.outputInUI}}
					</a>
					<ToolTipComponent
						@text="Click here to add a metric or set a fixed value"
						@position="top"
					/>
				</div>
			{{else if @model.isMissing}}
				<div class="type-metric type-missing type-mk">
					<a role="button" class="script-item__box" {{on "click" this.deleteMetric}}>
						<CommonUiIconComponent @name="Trash" @size="sm" />
					</a>
				</div>
			{{else}}
				{{@model.outputInUI}}
			{{/if}}
		</div>
	</template>
}
